import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import { Theme, useTheme } from '@mui/material/styles';
import React, { ReactEventHandler, useCallback, useState } from 'react';
import {
  CustomConfirmDialogProps,
  OkCancelConfirmDialogProps,
  YesNoConfirmDialogProps,
} from '../../Button/ETOButtonCommonProps';
import ConfirmDialog from '../../ConfirmDialog/ConfirmDialog';
import OkCancelConfirmDialog from '../../ConfirmDialog/OkCancelConfirmDialog';
import YesNoConfirmDialog from '../../ConfirmDialog/YesNoConfirmDialog';

interface ModalNavbarSubItemProps {
  onlyCustomComponent?: boolean;
  modalRef?: React.RefObject<HTMLButtonElement>;
  setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  customComponent?: React.ReactElement;
  componentName: string;
  icon: React.ReactNode;
  handleClick?: ReactEventHandler | any;
  confirm?:
    | CustomConfirmDialogProps
    | YesNoConfirmDialogProps
    | OkCancelConfirmDialogProps;
}

const menuItemSx = (theme: Theme) => ({
  padding: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    boxShadow: 'none',
  },
  // Outer button Container
  [theme.breakpoints.down('sm')]: {
    minWidth: theme.spacing(4),
    height: theme.spacing(4),
  },
  minWidth: theme.spacing(4.5),
  height: theme.spacing(4.5),

  // Fonts
  ...theme.typography.subtitle2,
  [theme.breakpoints.up('xs')]: {
    ...theme.typography.subtitle1,
  },
  // Icon
  '& svg': {
    display: 'block',
    fontSize: 'large',
  },
  '&:hover ': {
    backgroundColor: theme.palette.action.focus,
  },
});

const subMenuItemButtonSx = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  '& button': {
    boxShadow: 'none',
    maxHeight: theme.spacing(5),
    textTransform: 'capitalize',
  },
  '& button:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.text.primary,
  },
});

const ModalNavbarSubItem = (props: ModalNavbarSubItemProps) => {
  const {
    icon,
    confirm,
    componentName,
    handleClick,
    customComponent,
    onlyCustomComponent,
    setIsModalOpen,
  } = props;
  const theme = useTheme();

  const [dialogOpen, setDialogOpen] = useState(false);

  const _onDialogLeft = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | undefined) => {
      setDialogOpen(false);
      if (confirm && confirm.type === 'yesNo') {
        confirm.onNo(e);
      } else if (confirm && confirm.type === 'custom') {
        confirm.leftButton?.onClick(e, undefined);
      }
    },
    [],
  );

  const _onDialogRight = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | undefined) => {
      if (handleClick) {
        handleClick(e);
        setDialogOpen(false);
      }
    },
    [],
  );

  const _renderConfirm = useCallback(() => {
    if (confirm) {
      switch (confirm.type) {
        case 'yesNo':
          return (
            <YesNoConfirmDialog
              open={dialogOpen}
              title={confirm.title}
              content={confirm.content}
              data={confirm.data}
              onYes={_onDialogRight}
              onNo={_onDialogLeft}
            />
          );
        case 'okCancel':
          return (
            <OkCancelConfirmDialog
              open={dialogOpen}
              title={confirm.title}
              content={confirm.content}
              data={confirm.data}
              onOk={_onDialogRight}
              onCancel={() => _onDialogLeft(undefined)}
            />
          );
        case 'custom':
          // eslint-disable-next-line react/jsx-props-no-spreading
          return <ConfirmDialog {...confirm} open={dialogOpen} />;
        default:
          // eslint-disable-next-line react/jsx-no-useless-fragment
          return <></>;
      }
    }

    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }, [confirm, dialogOpen]);

  const _onClick = useCallback(
    (e: React.MouseEvent<HTMLLIElement, MouseEvent> | undefined) => {
      if (handleClick) {
        if (confirm) {
          setDialogOpen(true);
        } else {
          handleClick(e);
        }
      }
    },
    [handleClick, setDialogOpen, dialogOpen, confirm],
  );

  return (
    <>
      {!onlyCustomComponent && (
        <MenuItem
          sx={[menuItemSx, subMenuItemButtonSx]}
          onClick={(e) => _onClick(e)}
        >
          <Box sx={{ marginRight: theme.spacing(0.5) }}>{icon}</Box>
          {componentName}
        </MenuItem>
      )}
      {/* Must ensure customComponent is always a ReactElement/Node */}
      {Boolean(customComponent)
        && customComponent
        && React.cloneElement(customComponent, {
          setIsModalOpen,
        })}
      {confirm && _renderConfirm()}
    </>
  );
};

export default ModalNavbarSubItem;
